import React, { useState, useEffect } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, TextField } from "@mui/material";
import { Add, Delete, Edit, Save, Cancel } from "@mui/icons-material";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
const generateId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random()}`);
export default function AdditionalAndRevisedMeasuresTable({ additionalMeasures, refetchMonitoringData, vesselMstId, createDate }) {
	const [measures, setMeasures] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [deletingRowIndex, setDeletingRowIndex] = useState();
	const handleDeleteBtnClick = (rowIndex) => {
		setConfirmDelete(true);
		setDeletingRowIndex(rowIndex);
	};
	const [confirmDeleteStep, setConfirmDeleteStep] = useState(false);
	const [deletingStepInfo, setDeletingStepInfo] = useState({ measureId: null, stepId: null });
	const handleDeleteStepBtnClick = (measureId, stepId) => {
		setConfirmDeleteStep(true);
		setDeletingStepInfo({ measureId, stepId });
	};
	const [fieldErrors, setFieldErrors] = useState({});
	useEffect(() => {
		if (additionalMeasures?.length) {
			const formatted = additionalMeasures.map((m) => {
				const steps = [];
				const impediments = [];

				(m.implementationSteps || []).forEach((step) => {
					const commonId = step.id?.toString() || generateId();

					steps.push({
						id: commonId,
						milestone: step.milestone,
						due: step.dueDate ? step.dueDate.split("T")[0] : "",
						responsible: step.responsible,
						isEditing: false,
					});

					impediments.push({
						id: commonId,
						impediment: step.impediment,
						contingency: step.contingencies,
						isEditing: false,
					});
				});

				return {
					id: m.id?.toString() || generateId(),
					measure: m.measure,
					impact: m.impactOnCii,
					steps,
					impediments,
					isEditing: false,
					isNew: false,
				};
			});

			setMeasures(formatted);
		}
	}, [additionalMeasures]);

	const saveMeasure = async (measureData) => {
		if (measureData.steps.length === 0) {
			emitToasterMsg(
				"error",
				"Table 6.3",
				"At least one time & method of implementation and responsible personnel, impediments and contingency measures is required to save the measure."
			);
			return;
		}
		setIsSaving(true);
		try {
			const payload = measures.map((measureData) => ({
				measure: measureData.measure,
				impactOnCii: measureData.impact,
				implementationSteps: measureData.steps.map((step, index) => ({
					milestone: step.milestone,
					dueDate: step.due ? new Date(step.due).toISOString() : null,
					responsible: step.responsible,
					impediment: measureData.impediments[index]?.impediment || "",
					contingencies: measureData.impediments[index]?.contingency || "",
				})),
			}));

			const res = await callApi({
				method: "POST",
				url: API_ROUTES.AdditionalMeasures(vesselMstId),
				data: payload,
			});
			if (res?.isSuccess) {
				emitToasterMsg("success", "Table 6.3", "List of additional Measures is saved successfully");
				setMeasures(measures.map((m) => (m.id === measureData.id ? { ...m, isEditing: false, isNew: false } : m)));
				await refetchMonitoringData?.();
			} else {
				emitToasterMsg("error", "Table 6.3", res?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table 6.3.", error.errorMessage);
		} finally {
			setIsSaving(false);
		}
	};
	const buildPayload = (data) =>
		data.map((m) => ({
			measure: m.measure,
			impactOnCii: m.impact,
			implementationSteps: m.steps.map((step) => ({
				milestone: step.milestone,
				dueDate: step.due ? new Date(step.due).toISOString() : null,
				responsible: step.responsible,
				impediment: m.impediments.find((imp) => imp.id === step.id)?.impediment || "",
				contingencies: m.impediments.find((imp) => imp.id === step.id)?.contingency || "",
			})),
		}));

	const handleAddMeasure = () => {
		const newId = generateId();
		const newStepId = generateId();
		const newMeasure = {
			id: newId,
			measure: "",
			impact: "",
			steps: [
				{
					id: newStepId,
					milestone: "",
					due: "",
					responsible: "",
					isNew: true,
					isEditing: true,
				},
			],
			impediments: [
				{
					id: newStepId,
					impediment: "",
					contingency: "",
					isNew: true,
					isEditing: true,
				},
			],
			isEditing: true,
			isNew: true,
		};
		setMeasures([...measures, newMeasure]);
	};

	const handleSaveMeasure = (measure) => {
		const hasUnsavedSteps = measure.steps.some((step) => step.isEditing);
		const hasUnsavedImpediments = measure.impediments.some((imp) => imp.isEditing);

		if (hasUnsavedSteps || hasUnsavedImpediments) {
			emitToasterMsg(
				"error",
				"Please save all time & method of implementation and responsible personnel, impediments and contingency measures before saving the item."
			);
			return;
		}
		const errors = {};

		if (!measure.measure.trim()) {
			errors.measure = "Field is required";
		}

		if (!measure.impact.trim()) {
			errors.impact = "Field is required";
		}
		if (Object.keys(errors).length > 0) {
			setFieldErrors((prev) => ({ ...prev, [measure.id]: errors }));
			return;
		}

		setFieldErrors((prev) => ({ ...prev, [measure.id]: {} }));
		saveMeasure(measure);
	};

	const handleCancelMeasure = (id) => {
		setMeasures((prev) =>
			prev.reduce((acc, m) => {
				if (m.id !== id) {
					acc.push(m);
				} else if (m.isNew) {
				} else if (m.isEditing) {
					acc.push({ ...m, isEditing: false });
				} else {
					acc.push(m);
				}
				return acc;
			}, [])
		);
	};

	const handleDeleteMeasure = async (id) => {
		const updatedData = measures.filter((m) => m.id.toString().trim() !== id.toString().trim());

		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.AdditionalMeasures(vesselMstId),
				data: buildPayload(updatedData),
			});

			if (res?.isSuccess) {
				emitToasterMsg("success", "Table 6.3", "List of additional Measures deleted successfully");
				setMeasures(updatedData);
				setConfirmDelete(false);
				await refetchMonitoringData?.();
			} else {
				emitToasterMsg("error", "Table 6.3", res?.errorMessage);
				setConfirmDelete(false);
			}
		} catch (error) {
			emitToasterMsg("error", "Table 6.3", error.errorMessage);
		}
	};

	const handleAddStep = (measureId) => {
		const newId = generateId();
		setMeasures(
			measures.map((m) =>
				m.id === measureId
					? {
							...m,
							steps: [
								...m.steps,
								{
									id: newId,
									milestone: "",
									due: "",
									responsible: "",
									isNew: true,
									isEditing: true,
								},
							],
							impediments: [
								...m.impediments,
								{
									id: newId,
									impediment: "",
									contingency: "",
									isNew: true,
									isEditing: true,
								},
							],
					  }
					: m
			)
		);
	};

	const handleSaveStep = (measureId, stepId) => {
		const measure = measures.find((m) => m.id === measureId);
		const step = measure.steps.find((s) => s.id === stepId);
		const impediment = measure.impediments.find((imp) => imp.id === stepId);
		const errors = {};

		if (!step.milestone.trim()) {
			errors.milestone = "Field is required";
		}

		if (!step.due.trim()) {
			errors.due = "Field is required";
		}

		if (!step.responsible.trim()) {
			errors.responsible = "Field is required";
		}

		if (!impediment.impediment.trim()) {
			errors.impediment = "Field is required";
		}

		if (!impediment.contingency.trim()) {
			errors.contingency = "Field is required";
		}

		if (Object.keys(errors).length > 0) {
			setFieldErrors((prev) => ({
				...prev,
				[`${measureId}_${stepId}`]: errors,
			}));
			return;
		}

		setFieldErrors((prev) => ({ ...prev, [`${measureId}_${stepId}`]: {} }));

		setMeasures(
			measures.map((m) =>
				m.id === measureId
					? {
							...m,
							steps: m.steps.map((s) => (s.id === stepId ? { ...s, isEditing: false, isNew: false, originalData: undefined } : s)),
							impediments: m.impediments.map((imp) =>
								imp.id === stepId ? { ...imp, isEditing: false, isNew: false, originalData: undefined } : imp
							),
					  }
					: m
			)
		);
	};

	const handleCancelStep = (measureId, stepId) => {
		setMeasures((prev) =>
			prev.map((m) => {
				if (m.id !== measureId) return m;

				return {
					...m,
					steps: m.steps.reduce((acc, s) => {
						if (s.id !== stepId) {
							acc.push(s);
						} else if (s.isNew) {
						} else if (s.isEditing) {
							acc.push({ ...s.originalData, isEditing: false });
						} else {
							acc.push(s);
						}
						return acc;
					}, []),

					impediments: m.impediments.reduce((acc, imp) => {
						if (imp.id !== stepId) {
							acc.push(imp);
						} else if (imp.isNew) {
						} else if (imp.isEditing) {
							acc.push({ ...imp, isEditing: false });
						} else {
							acc.push(imp);
						}
						return acc;
					}, []),
				};
			})
		);
	};

	const handleDeleteStep = (measureId, stepId) => {
		setMeasures(
			measures.map((m) =>
				m.id === measureId ? { ...m, steps: m.steps.filter((s) => s.id !== stepId), impediments: m.impediments.filter((imp) => imp.id !== stepId) } : m
			)
		);
	};
	const handleEditStep = (measureId, stepId) => {
		setMeasures(
			measures.map((m) =>
				m.id === measureId
					? {
							...m,
							steps: m.steps.map((s) => (s.id === stepId ? { ...s, isEditing: true, originalData: { ...s } } : s)),
							impediments: m.impediments.map((imp) => (imp.id === stepId ? { ...imp, isEditing: true, originalData: { ...imp } } : imp)),
					  }
					: m
			)
		);
	};

	const stepColumns = (measureId) => [
		{
			header: "Actions",
			Cell: ({ row }) => {
				const step = row.original;
				const parentMeasure = measures.find((m) => m.id === measureId);
				return step.isEditing ? (
					<>
						<Box sx={{ display: "flex", gap: "1rem", marginLeft: "10px" }}>
							<IconButton style={{ padding: "0px" }} onClick={() => handleCancelStep(measureId, step.id)}>
								<Cancel fontSize="small" />
							</IconButton>
							<IconButton style={{ padding: "0px" }} onClick={() => handleSaveStep(measureId, step.id)} color="primary">
								<Save fontSize="small" />
							</IconButton>
						</Box>
					</>
				) : (
					<>
						<Box sx={{ display: "flex", gap: "1rem", marginLeft: "10px" }}>
							<IconButton style={{ padding: "0px" }} onClick={() => handleEditStep(measureId, step.id)} disabled={!parentMeasure?.isEditing}>
								<Edit fontSize="small" />
							</IconButton>
							<IconButton
								style={{ padding: "0px" }}
								color="error"
								onClick={() => handleDeleteStepBtnClick(measureId, step.id)}
								disabled={!parentMeasure?.isEditing}
							>
								<Delete fontSize="small" />
							</IconButton>
						</Box>
					</>
				);
			},
		},
		{
			header: "Milestone",
			accessorKey: "milestone",
			Cell: ({ row }) =>
				row.original.isEditing ? (
					<TextField
						variant="standard"
						size="small"
						value={row.original.milestone}
						error={!!fieldErrors[`${measureId}_${row.original.id}`]?.milestone}
						helperText={fieldErrors[`${measureId}_${row.original.id}`]?.milestone || ""}
						onChange={(e) => {
							setMeasures(
								measures.map((m) =>
									m.id === measureId
										? {
												...m,
												steps: m.steps.map((s) => (s.id === row.original.id ? { ...s, milestone: e.target.value } : s)),
										  }
										: m
								)
							);
						}}
					/>
				) : (
					row.original.milestone
				),
		},
		{
			header: "Due",
			accessorKey: "due",
			Cell: ({ row }) => {				
				let minDate;
				if(createDate===null || createDate===undefined){
				  minDate="";				 
				}else{
                  minDate = new Date(createDate).toISOString().split("T")[0];				
				}
				return row.original.isEditing ? (
					<TextField
						type="date"
						variant="standard"
						size="small"
						required
						value={row.original.due ?? ""}
						error={!!fieldErrors[`${measureId}_${row.original.id}`]?.due}
						helperText={fieldErrors[`${measureId}_${row.original.id}`]?.due || ""}
						inputProps={{
							min: minDate, 
						}}
						onChange={(e) => {
							const selected = e.target.value;

							if (selected < minDate) {

								setFieldErrors((prev) => ({
									...prev,
									[`${measureId}_${row.original.id}`]: {
										...(prev?.[`${measureId}_${row.original.id}`] || {}),
										due: `Due date cannot be before ${minDate}`,
									},
								}));
								return;
							}

							setFieldErrors((prev) => {
								const key = `${measureId}_${row.original.id}`;
								if (!prev?.[key]) return prev;

								const { due, ...rest } = prev[key];
								const updated = { ...prev, [key]: rest };
								if (Object.keys(rest).length === 0) delete updated[key];
								return updated;
							});

							setMeasures(
								measures.map((m) =>
									m.id === measureId
										? {
												...m,
												steps: m.steps.map((s) => (s.id === row.original.id ? { ...s, due: selected } : s)),
										  }
										: m
								)
							);
						}}
					/>
				) : (
					row.original.due
				);
			},
		},
		{
			header: "Responsible",
			accessorKey: "responsible",
			Cell: ({ row }) =>
				row.original.isEditing ? (
					<TextField
						variant="standard"
						size="small"
						value={row.original.responsible}
						error={!!fieldErrors[`${measureId}_${row.original.id}`]?.responsible}
						helperText={fieldErrors[`${measureId}_${row.original.id}`]?.responsible || ""}
						onChange={(e) => {
							setMeasures(
								measures.map((m) =>
									m.id === measureId
										? {
												...m,
												steps: m.steps.map((s) => (s.id === row.original.id ? { ...s, responsible: e.target.value } : s)),
										  }
										: m
								)
							);
						}}
					/>
				) : (
					row.original.responsible
				),
		},
	];

	const impedimentColumns = (measureId) => [
		{
			header: "Impediment",
			accessorKey: "impediment",
			Cell: ({ row }) =>
				row.original.isEditing ? (
					<TextField
						variant="standard"
						size="small"
						value={row.original.impediment}
						error={!!fieldErrors[`${measureId}_${row.original.id}`]?.impediment}
						helperText={fieldErrors[`${measureId}_${row.original.id}`]?.impediment || ""}
						onChange={(e) => {
							setMeasures(
								measures.map((m) =>
									m.id === measureId
										? {
												...m,
												impediments: m.impediments.map((imp) =>
													imp.id === row.original.id ? { ...imp, impediment: e.target.value } : imp
												),
										  }
										: m
								)
							);
						}}
					/>
				) : (
					row.original.impediment
				),
		},
		{
			header: "Contingencies",
			accessorKey: "contingency",
			Cell: ({ row }) =>
				row.original.isEditing ? (
					<TextField
						variant="standard"
						size="small"
						value={row.original.contingency}
						error={!!fieldErrors[`${measureId}_${row.original.id}`]?.contingency}
						helperText={fieldErrors[`${measureId}_${row.original.id}`]?.contingency || ""}
						onChange={(e) => {
							setMeasures(
								measures.map((m) =>
									m.id === measureId
										? {
												...m,
												impediments: m.impediments.map((imp) =>
													imp.id === row.original.id ? { ...imp, contingency: e.target.value } : imp
												),
										  }
										: m
								)
							);
						}}
					/>
				) : (
					row.original.contingency
				),
		},
	];

	const sharedMuiProps = {
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": { borderRight: "none" },
			},
		},
		muiTableBodyCellProps: ({ row, cell }) => ({
			sx: {
				borderRight: "1px solid lightgray",
				"&:last-child": { borderRight: "none" },
			},
		}),
		muiTableBodyRowProps: { sx: { "&:not(:last-of-type)": { borderBottom: "1px solid lightgray" }, verticalAlign: "top" } },
		muiTablePaperProps: { sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" } },
		muiTableContainerProps: { sx: { maxHeight: "520px" } },
		muiTableHeadRowProps: { sx: { borderBottom: "2px solid lightgray" } },
	};

	const rowHeightStyle = {
		muiTableBodyRowProps: {
			sx: {
				height: "100px",
				minHeight: "88px",
				verticalAlign: "top",
				"&:not(:last-of-type)": {
					borderBottom: "1px solid lightgray",
				},
			},
		},
	};

	const columns = [
		{
			header: "Actions",
			Cell: ({ row }) => {
				const measure = row.original;
				return measure.isEditing ? (
					<>
						<IconButton onClick={() => handleCancelMeasure(measure.id)} disabled={isSaving}>
							<Cancel />
						</IconButton>
						<IconButton onClick={() => handleSaveMeasure(measure)} disabled={isSaving} color="primary">
							<Save />
						</IconButton>
					</>
				) : (
					<>
						<IconButton
							onClick={() => {
								setMeasures(measures.map((m) => (m.id === measure.id ? { ...m, isEditing: true } : m)));
							}}
						>
							<Edit />
						</IconButton>
						<IconButton color="error" onClick={() => handleDeleteBtnClick(measure.id)}>
							<Delete />
						</IconButton>
					</>
				);
			},
		},
		{
			header: "Measure",
			accessorKey: "measure",
			Cell: ({ row }) =>
				row.original.isEditing ? (
					<TextField
						variant="standard"
						size="small"
						value={row.original.measure}
						error={!!fieldErrors[row.original.id]?.measure}
						helperText={fieldErrors[row.original.id]?.measure || ""}
						onChange={(e) => {
							setMeasures(measures.map((m) => (m.id === row.original.id ? { ...m, measure: e.target.value } : m)));
						}}
					/>
				) : (
					row.original.measure
				),
		},
		{
			header: "Impact on CII",
			accessorKey: "impact",
			Cell: ({ row }) =>
				row.original.isEditing ? (
					<TextField
						variant="standard"
						size="small"
						value={row.original.impact}
						error={!!fieldErrors[row.original.id]?.impact}
						helperText={fieldErrors[row.original.id]?.impact || ""}
						onChange={(e) => {
							setMeasures(measures.map((m) => (m.id === row.original.id ? { ...m, impact: e.target.value } : m)));
						}}
					/>
				) : (
					row.original.impact
				),
		},
		{
			header: "Time and method of implementation and responsible personnel",
			Cell: ({ row }) => (
				<Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
					<Box sx={{ mt: 1 }}>
						<Button
							startIcon={<Add />}
							onClick={() => handleAddStep(row.original.id)}
							size="small"
							disabled={
								!row.original.isEditing || row.original.steps.some((s) => s.isEditing) || row.original.impediments.some((imp) => imp.isEditing)
							}
							sx={{
								color: "rgb(255 255 255)",
								backgroundColor: "#042a4c",
								"&:hover": {
									backgroundColor: "#031f3d",
								},
							}}
						>
							Add
						</Button>
					</Box>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<MaterialReactTable
							columns={stepColumns(row.original.id)}
							data={row.original.steps}
							enableStickyHeader
							enableSorting={false}
							enableRowSelection={false}
							enableColumnPinning={false}
							enableTopToolbar={false}
							enableBottomToolbar={false}
							enableColumnActions={false}
							enableGlobalFilter={false}
							enableColumnFilters={false}
							enablePagination={false}
							enableDensityToggle={false}
							enableFullScreenToggle={false}
							enableColumnVisibility={false}
							enableHiding={false}
							muiTableBodyRowProps={rowHeightStyle.muiTableBodyRowProps}
							{...sharedMuiProps}
						/>
					</Box>
				</Box>
			),
		},
		{
			header: "Impediments and contingency measures",
			Cell: ({ row }) => (
				<MaterialReactTable
					columns={impedimentColumns(row.original.id)}
					data={row.original.impediments}
					enableStickyHeader={true}
					enableSorting={false}
					enableRowSelection={false}
					enableColumnPinning={false}
					enableTopToolbar={false}
					enableBottomToolbar={false}
					enableColumnActions={false}
					enableGlobalFilter={false}
					enableColumnFilters={false}
					createDisplayMode={"row"}
					editDisplayMode={"row"}
					enablePagination={false}
					enableDensityToggle={false}
					enableFullScreenToggle={false}
					enableColumnVisibility={false}
					enableHiding={false}
					muiTableBodyRowProps={rowHeightStyle.muiTableBodyRowProps}
					{...sharedMuiProps}
				/>
			),
		},
	];

	const table = useMaterialReactTable({
		columns,
		data: measures || [],
		getRowId: (originalRow) => originalRow.id,
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		enableColumnActions: false,
		enableGlobalFilter: false,
		enableColumnFilters: false,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enablePagination: false,
		enableDensityToggle: false,
		enableFullScreenToggle: false,
		enableColumnVisibility: false,
		enableHiding: false,
		...sharedMuiProps,
		renderTopToolbarCustomActions: () => (
			<Button startIcon={<Icon icon={ICONS.ADD} />} onClick={handleAddMeasure} disabled={measures.some((m) => m.isEditing)}>
				Add Measure
			</Button>
		),
	});

	return (
		<>
			<Box>
				<MaterialReactTable table={table} />
			</Box>
			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => {
									handleDeleteMeasure(deletingRowIndex);
								}}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>

							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this measure?</div>
				</Modal>
			</div>
			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDeleteStep}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => setConfirmDeleteStep(false)}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => {
									handleDeleteStep(deletingStepInfo.measureId, deletingStepInfo.stepId);
									setConfirmDeleteStep(false);
								}}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>

							<Button className="modal-cancel-btn" onClick={() => setConfirmDeleteStep(false)}>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this item?</div>
				</Modal>
			</div>
		</>
	);
}
